import * as BezierEasing from 'bezier-easing';

import { Injectable } from '@angular/core';

import { CordovaService } from '../../core/cordova.service';
import { BasicService } from './basic.service';

/**
 * This service handles adjusting the height of the current page when the keyboard is shown/hidden,
 * with an animation that tries to be the same as the keyboard animation (handled by iOS),
 * so that it looks like the keyboard is pushing the page up (and pulling it down when hiding).
 * The scroll of the page is also adjusted so that the page content is pushed up too:
 *  - for the text control docked at the bottom of the task process page
 *    (that is, the private editor), the content is always scrolled;
 *  - for other text controls that sit within their ion-content, including the inline public editor,
 *    the scroll is only adjusted so that the botton of the text control doesn't get hidden by the keyboard
 *  - the change password modal isn't scrolled at all by this service,
 *    relying instead on ion-input to keep inputs visible
 *    (but the --alc-keyboard-offset is still updated in that case,
 *     as the modal needs to reduce its size when the keyboard is shown so that the content can scroll up)
 */
@Injectable({
  providedIn: 'root',
})
export class KeyboardShowHideService {
  private ANIMATION_DURATION_MS = 450;

  private easing = BezierEasing(0.1, 1, 0.25, 1);

  private startTime;
  private keyboardHeightFrom = 0; // kebyoard is hidden when the user first clicks on a text control
  private keyboardHeightTo: number;

  private scrollFrom: number;
  private scrollTo: number;
  private previousScrollDelta: number;

  private innerScrollElement: HTMLElement;
  private elementToKeepInView: HTMLElement; // an element which should not be pushed out of view when adjusting the scroll
  // (a text input element, or a message bubble if replying to a private message)

  constructor(private cordovaService: CordovaService, private basicService: BasicService) {
    this.cordovaService.keyboardWillShow$.subscribe(keyboardSize => {
      // do not animate anything if the keyboard is already shown
      if (this.keyboardHeightFrom !== keyboardSize) {
        this.animateTo(keyboardSize);
      }
    });
    this.cordovaService.keyboardWillHide$.subscribe(() => {
      this.animateTo(0);
    });
  }

  /**
   * Registers an input control and its enclosing scroll element
   * which will be used to adjust the scroll when the keyboard shows/hides
   */
  setScrollElementAndTextInputElement(innerScrollElement, elementToKeepInView) {
    this.innerScrollElement = innerScrollElement;
    this.elementToKeepInView = elementToKeepInView;
  }

  /**
   * Seems cleaner to call this when leaving the page,
   * so that it can be garbage collected before another page calls setPageAndTextarea()
   */
  clearScrollElementAndTextInputElement() {
    this.elementToKeepInView = null;
    this.innerScrollElement = null;
  }

  private animateTo(keyboardHeightTarget: number) {
    this.startTime = performance.now();
    this.keyboardHeightTo = keyboardHeightTarget;
    if (this.innerScrollElement) {
      // The password-change modal doesn't set innerScrollElement and textInputElement
      this.initScrollParameters(keyboardHeightTarget);
    }
    requestAnimationFrame(() => this.animateInFrame());
  }

  /**
   * For the textarea docked at the bottom of the task process page,
   * we want to give the impression that the keyboard pushes the scrollable content
   * (this is inspired from WhatsApp on iOS),
   * and hence adjust the scroll by as much as we reduce the page height.
   * For other inputs and textareas, we want to make sure that the text control
   * is not hidden by the keyboard
   *
   * target = 0 if the keyboard is closing
   * target = keyboard height if keyboard is opening
   */
  private initScrollParameters(keyboardHeightTarget: number) {
    this.scrollFrom = this.innerScrollElement.scrollTop;
    if (keyboardHeightTarget > 0) {
      // keyboard is opening
      if (!this.elementToKeepInView) {
        // scroll as much as keyboard size
        this.scrollTo = this.scrollFrom + keyboardHeightTarget;
      } else {
        this.initScrollWithElementToKeepInView(keyboardHeightTarget);
      }
      this.previousScrollDelta = this.scrollTo - this.scrollFrom;
    } else {
      // keyboard closing, let's just scroll down as much as we scrolled up when the keyboard opened
      this.scrollTo = this.scrollFrom - this.previousScrollDelta;
    }
  }

  private initScrollWithElementToKeepInView(keyboardHeightTarget: number) {
    const distanceTarget = this.cordovaService.isNativeIOS() ? keyboardHeightTarget : 0;
    const distanceToBottom = this.distanceFromBottomOfElementToKeepInViewToBottomOfScrollContainer();
    if (distanceToBottom < distanceTarget) {
      // let's scroll if the keyboard would hide the text input control
      this.scrollTo = this.scrollFrom + distanceTarget - distanceToBottom;
    } else {
      // but let's not scroll at all if it would remain visible
      // (we could scroll to move down the control close to the keybard, but that might be more annying than anything else)
      this.scrollTo = this.scrollFrom;
    }
  }

  private animateInFrame() {
    // The page can be null if a modal is destroyed faster than the animation (in which case we just stop the recursion)
    let didRecurse = false;
    const targetDeltaHeight = this.keyboardHeightTo - this.keyboardHeightFrom;
    const targetDeltaScroll = this.scrollTo - this.scrollFrom;
    const currentDeltaT = performance.now() - this.startTime;
    if (currentDeltaT < this.ANIMATION_DURATION_MS) {
      const progress = currentDeltaT / this.ANIMATION_DURATION_MS;
      const easedProgress = this.easing(progress);
      this.updateStyle(targetDeltaHeight * easedProgress, targetDeltaScroll * easedProgress);
      requestAnimationFrame(() => this.animateInFrame());
      didRecurse = true;
    }
    if (!didRecurse) {
      // at the end of the recursion, do update style one last time in case the animation's framerate was low
      // (which can easily be the case if we click on the « take photo» button while the keyboard is displayed),
      // or in case the modal that showed the keyboard got closed
      this.updateStyle(targetDeltaHeight, targetDeltaScroll);
      this.keyboardHeightFrom = this.keyboardHeightTo; // store it for next time the keyboard is shown/hidden
    }
  }

  /**
   * Computes the position of the bottom of the input control (or the processed message if we are replying to a private task),
   * relative to the bottom of the scroll container's view.
   * The returned value is positive if the bottom of the text input control is above the container's viewport bottom edge,
   * negative otherwise.
   */
  private distanceFromBottomOfElementToKeepInViewToBottomOfScrollContainer() {
    const container = this.innerScrollElement;
    const element = this.elementToKeepInView;

    let containerPadding = 0;
    try {
      // on the task process page, the scrollcontainer has an annoying padding,
      // set as a CSS variable on the <ion-content>
      containerPadding = Number((container.offsetParent as any).style.getPropertyValue('--offset-top').split('px')[0]);
    } catch (e) {
      // ignore exception for now
      console.warn(e);
    }

    return container.scrollTop + container.clientHeight - containerPadding - (element.offsetTop + element.offsetHeight);
  }

  private updateStyle(deltaHeight: number, deltaScroll: number) {
    const htmlElement = this.basicService.document.documentElement;
    if (this.cordovaService.isNativeIOS()) {
      htmlElement.style.setProperty('--alc-keyboard-offset', `${this.keyboardHeightFrom + deltaHeight}px`);
    }
    if (this.innerScrollElement) {
      this.innerScrollElement.scrollTop = this.scrollFrom + deltaScroll;
    }
  }
}
